// A row's "…" menu: the details and the rare actions a row does not need on its
// face. Closes on Escape, on a click outside, and after an item is chosen.

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon.tsx';

export interface RowMenuItem {
  readonly label: string;
  readonly onSelect: () => void;
  readonly danger?: boolean;
  readonly disabled?: boolean;
  readonly testId?: string;
}

export function RowMenu({
  label,
  notes = [],
  items = [],
  testId,
}: {
  /** The button's accessible name, e.g. "More for Uploading in the dashboard". */
  readonly label: string;
  /** Plain lines shown at the top of the menu (dates, details). */
  readonly notes?: readonly string[];
  readonly items?: readonly RowMenuItem[];
  readonly testId?: string;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent) => {
      if (root.current !== null && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="row-menu" ref={root}>
      <button
        type="button"
        className="icon-button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-testid={testId}
        onClick={() => setOpen(!open)}
      >
        <Icon name="more" size={18} />
      </button>
      {open ? (
        <div className="row-menu-panel" role="menu" id={menuId} aria-label={label}>
          {notes.map((note) => (
            <p key={note} className="row-menu-note">
              {note}
            </p>
          ))}
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={`row-menu-item${item.danger === true ? ' danger' : ''}`}
              disabled={item.disabled}
              data-testid={item.testId}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
